import React, { useEffect, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { ReactComponent as Location } from "../../Global Icons/location.svg";
import { useDispatch, useSelector } from "react-redux";
import { viewLocality } from "../../Redux/Actions/localityAction";
import { useNavigate, useParams } from "react-router-dom";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import "./addLocality.css";
import "./viewLocality.css";

const mapContainerStyle = {
    width: "100%",
    height: "400px",
    marginTop: "10px",
    borderRadius: "8px",
};

const libraries = ["places"];

const ViewLocality = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const { locality, singleLoading } = useSelector((state) => state.locality || {});
    const { cities = { data: [] } } = useSelector((state) => state.city || {});

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries: libraries,
    });

    const [mapCenter, setMapCenter] = useState({ lat: 28.6139, lng: 77.209 });
    const [markerPosition, setMarkerPosition] = useState(null);

    // Fetch locality data on component mount
    useEffect(() => {
        dispatch({ type: "RESET_LOCALITY" });
        
        if (id) {
            dispatch(viewLocality(id));
        }
        
        return () => {
            dispatch({ type: "RESET_LOCALITY" });
        };
    }, [id, dispatch]);

    // Update map when locality data is loaded
    useEffect(() => {
        if (locality && locality.data) {
            const coordinates = locality.data.location?.coordinates || [0, 0];
            const lat = coordinates[1] || 0;
            const lng = coordinates[0] || 0;

            if (lat !== 0 && lng !== 0) {
                setMapCenter({ lat, lng });
                setMarkerPosition({ lat, lng });
            }
        }
    }, [locality]);

    const getCityName = () => {
        if (!locality?.data?.city) return "N/A";
        
        // If city is populated object
        if (typeof locality.data.city === 'object' && locality.data.city.city_name) {
            return locality.data.city.city_name;
        }
        
        // If city is just an ID, find it in cities array
        const cityId = locality.data.city;
        const city = cities.data.find(c => c._id === cityId);
        return city ? city.city_name : cityId;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        return (
            <span className={`status-badge ${status || 'active'}`}>
                {status || 'Active'}
            </span>
        );
    };

    if (singleLoading) {
        return (
            <MainLayout>
                <div className="franchise-form-container">
                    <div className="franchise-form-wrapper franchise-card">
                        <div className="franchise-card-content">
                            <div className="view-locality-loading">
                                <div className="view-locality-loading-icon">🗺️</div>
                                <p className="view-locality-loading-text">
                                    Loading locality details...
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    if (!locality || !locality.data) {
        return (
            <MainLayout>
                <div className="franchise-form-container">
                    <div className="franchise-form-wrapper franchise-card">
                        <div className="franchise-card-content">
                            <div className="view-locality-not-found">
                                <div className="view-locality-error-icon">⚠️</div>
                                <p className="view-locality-error-text">
                                    Locality not found
                                </p>
                                <button
                                    onClick={() => navigate("/locality")}
                                    className="view-locality-back-button"
                                >
                                    Back to List
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    const localityData = locality.data;

    return (
        <MainLayout>
            <div className="franchise-form-container">
                <div className="franchise-form-wrapper franchise-card">
                    <div className="franchise-card-content">
                        {/* Header */}
                        <div className="franchise-header view-locality-header">
                            <div className="franchise-header-icon">
                                <Location style={{ height: '30px', width: '30px' }} />
                            </div>
                            <div className="view-locality-header-content">
                                <h1>Locality Details</h1>
                                <p className="view-locality-header-subtitle">
                                    View complete information about this locality
                                </p>
                            </div>
                            <div>
                                {getStatusBadge(localityData.status)}
                            </div>
                        </div>

                        {/* Basic Information Section */}
                        <div className="view-locality-section">
                            <h3 className="section-header">
                                Basic Information
                            </h3>
                            
                            <div className="info-grid two-column">
                                <div className="info-field">
                                    <label>Locality Name</label>
                                    <p>{localityData.name || 'N/A'}</p>
                                </div>

                                <div className="info-field">
                                    <label>City</label>
                                    <p>{getCityName()}</p>
                                </div>

                                <div className="info-field small-text break-all">
                                    <label>Place ID</label>
                                    <p>{localityData.place_id || 'N/A'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Address Information Section */}
                        <div className="view-locality-section">
                            <h3 className="section-header">
                                Address Information
                            </h3>
                            
                            <div className="info-grid single-column">
                                <div className="info-field">
                                    <label>Formatted Address</label>
                                    <p>{localityData.formatted_address || 'N/A'}</p>
                                </div>

                                <div className="info-field">
                                    <label>Short Address</label>
                                    <p>{localityData.short_address || 'N/A'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Location Coordinates Section */}
                        <div className="view-locality-section">
                            <h3 className="section-header">
                                Location Coordinates
                            </h3>
                            
                            <div className="info-grid three-column">
                                <div className="info-field monospace">
                                    <label>Latitude</label>
                                    <p>{localityData.location?.coordinates?.[1]?.toFixed(6) || 'N/A'}</p>
                                </div>

                                <div className="info-field monospace">
                                    <label>Longitude</label>
                                    <p>{localityData.location?.coordinates?.[0]?.toFixed(6) || 'N/A'}</p>
                                </div>

                                <div className="info-field">
                                    <label>Google Maps</label>
                                    <p>
                                        <a 
                                            href={localityData.google_url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="google-maps-link"
                                        >
                                            📍 Open in Google Maps
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Map Section */}
                        <div className="view-locality-section">
                            <h3 className="section-header">
                                Map Location
                            </h3>

                            {loadError && (
                                <div className="map-error">
                                    <div className="map-icon">🗺️</div>
                                    <p className="map-text">Map unavailable</p>
                                </div>
                            )}

                            {!isLoaded && !loadError && (
                                <div className="map-loading">
                                    <div className="map-icon">🗺️</div>
                                    <p className="map-text">Loading map...</p>
                                </div>
                            )}

                            {isLoaded && !loadError && (
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={mapCenter}
                                    zoom={15}
                                    options={{
                                        disableDefaultUI: false,
                                        zoomControl: true,
                                        streetViewControl: true,
                                        mapTypeControl: true,
                                        fullscreenControl: true,
                                    }}
                                >
                                    {markerPosition && (
                                        <Marker 
                                            position={markerPosition}
                                            title={localityData.name}
                                        />
                                    )}
                                </GoogleMap>
                            )}
                        </div>

                        {/* Metadata Section */}
                        <div className="view-locality-section">
                            <h3 className="section-header">
                                Additional Information
                            </h3>
                            
                            <div className="info-grid two-column">
                                <div className="info-field date-text">
                                    <label>Created At</label>
                                    <p>{formatDate(localityData.created_at)}</p>
                                </div>

                                <div className="info-field date-text">
                                    <label>Last Updated</label>
                                    <p>{formatDate(localityData.updated_at)}</p>
                                </div>

                                {/* <div className="info-field small-text">
                                    <label>Locality ID</label>
                                    <p>{localityData._id || 'N/A'}</p>
                                </div> */}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="form-navigation view-locality-actions">
                            <button
                                type="button"
                                onClick={() => navigate("/locality")}
                                className="btn-back"
                            >
                                Back to List
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate(`/locality/edit/${id}`)}
                                className="btn-edit"
                            >
                                Edit Locality
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ViewLocality;